import { useState } from "react";
import LinkIcon from "@mui/icons-material/Link";
import { AttachRestaurantsDialog } from "./AttachRestaurantsDialog";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import { InfoCard } from "src/design/InfoCard";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import {
  colorTextPrimary,
  colorTextMuted,
  colorTextError,
  colorBorder,
  colorActionDanger,
  colorSurfaceDangerHover,
} from "src/design/tokens";
import {
  useOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
} from "src/data/admin/queries/organization";
import { OrganizationDto } from "src/data/admin/types/organization";

interface OrganizationRecapViewProps {
  orgId: string;
  onBack: () => void;
  onDeleted: () => void;
  onUpdated: (org: OrganizationDto) => void;
}

export const OrganizationRecapView = ({
  orgId,
  onBack,
  onDeleted,
  onUpdated,
}: OrganizationRecapViewProps) => {
  const [editOpen, setEditOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editOwnerId, setEditOwnerId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: org, isLoading } = useOrganization(orgId);

  const updateMutation = useUpdateOrganization((updated) => {
    setEditOpen(false);
    setSnack({ open: true, message: "Organization updated successfully.", severity: "success" });
    onUpdated(updated);
  });

  const deleteMutation = useDeleteOrganization(() => {
    setDeleteOpen(false);
    onDeleted();
  });

  const handleOpenEdit = () => {
    if (!org) return;
    setEditName(org.name);
    setEditOwnerId(org.ownerId ?? "");
    setEditOpen(true);
  };

  const handleUpdate = () => {
    if (!org || !editName.trim()) return;
    updateMutation.mutate(
      { id: org.id, payload: { name: editName.trim(), ownerId: editOwnerId.trim() || null } },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message: err instanceof Error ? err.message : "An error occurred",
            severity: "error",
          }),
      }
    );
  };

  const handleDelete = () => {
    if (!org) return;
    deleteMutation.mutate(org.id, {
      onError: (err) =>
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : "An error occurred",
          severity: "error",
        }),
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!org) {
    return (
      <Box>
        <Typography sx={{ color: colorTextMuted }}>Organization not found.</Typography>
        <CustomButton color="inverted" onClick={onBack} sx={{ mt: 2 }}>
          Back
        </CustomButton>
      </Box>
    );
  }

  const owner = org.ownerId
    ? org.staff.find((s) => s.id === org.ownerId)
    : null;

  return (
    <>
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colorTextPrimary }}>
            {org.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomButton
            color="inverted"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
            size="small"
          >
            Edit
          </CustomButton>
          <CustomButton
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteOpen(true)}
            size="small"
            sx={{
              color: colorActionDanger,
              border: `1px solid ${colorBorder}`,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: colorSurfaceDangerHover,
                borderColor: colorActionDanger,
              },
            }}
          >
            Delete
          </CustomButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* General */}
        <Grid item xs={12} md={6}>
          <InfoCard title="General" icon={<BusinessIcon fontSize="small" />}>
            <LabeledField label="ID" value={org.id} mono />
            <LabeledField
              label="Owner"
              value={
                owner
                  ? `${owner.name} (${owner.email})`
                  : org.ownerId
                  ? org.ownerId
                  : "—"
              }
              mono={!owner && !!org.ownerId}
            />
          </InfoCard>
        </Grid>

        {/* Staff */}
        <Grid item xs={12} md={6}>
          <InfoCard
            title={`Staff (${org.staff.length})`}
            icon={<PeopleOutlineIcon fontSize="small" />}
          >
            {org.staff.length === 0 ? (
              <EmptyState label="No staff members." />
            ) : (
              <SummaryTable
                columns={["Name", "Email", "ID"]}
                rows={org.staff.map((s) => [s.name, s.email, s.id])}
                monoCol={2}
              />
            )}
          </InfoCard>
        </Grid>

        {/* Restaurants */}
        <Grid item xs={12} md={6}>
          <InfoCard
            title={`Restaurants (${org.restaurants.length})`}
            icon={<RestaurantOutlinedIcon fontSize="small" />}
            action={
              <CustomButton
                color="inverted"
                startIcon={<LinkIcon />}
                size="small"
                onClick={() => setAttachOpen(true)}
              >
                Manage
              </CustomButton>
            }
          >
            {org.restaurants.length === 0 ? (
              <EmptyState label="No restaurants linked." />
            ) : (
              <SummaryTable
                columns={["Name", "City", "Quartier", "ID"]}
                rows={org.restaurants.map((r) => [r.name, r.city, r.quartier, r.id])}
                monoCol={3}
              />
            )}
          </InfoCard>
        </Grid>

        {/* Roles */}
        <Grid item xs={12} md={6}>
          <InfoCard
            title={`Roles (${org.roles.length})`}
            icon={<AdminPanelSettingsOutlinedIcon fontSize="small" />}
          >
            {org.roles.length === 0 ? (
              <EmptyState label="No roles defined." />
            ) : (
              <SummaryTable
                columns={["Name", "Description", "ID"]}
                rows={org.roles.map((r) => [r.name, r.description ?? "—", r.id])}
                monoCol={2}
              />
            )}
          </InfoCard>
        </Grid>
      </Grid>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
            />
            <TextField
              label="Owner ID (staff ID)"
              fullWidth
              size="small"
              value={editOwnerId}
              onChange={(e) => setEditOwnerId(e.target.value)}
              helperText={
                org.staff.length > 0
                  ? `Staff in this org: ${org.staff.map((s) => `${s.name} → ${s.id}`).join(", ")}`
                  : "Leave empty to clear the owner."
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setEditOpen(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </CustomButton>
          <CustomLoadingButton
            loading={updateMutation.isPending}
            disabled={!editName.trim()}
            onSubmit={handleUpdate}
            label="Save changes"
            color="success"
            fullwidth={false}
          />
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleteMutation.isPending && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{org.name}</strong>? This will
            also delete all staff and roles in this organization. Restaurants and
            permissions are preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setDeleteOpen(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            Delete
          </CustomButton>
        </DialogActions>
      </Dialog>

      <AttachRestaurantsDialog
        open={attachOpen}
        orgId={org.id}
        currentRestaurantIds={org.restaurants.map((r) => r.id)}
        onClose={() => setAttachOpen(false)}
        onSaved={() => {
          setAttachOpen(false);
          setSnack({ open: true, message: "Restaurants updated successfully.", severity: "success" });
        }}
      />

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

const LabeledField = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" sx={{ color: colorTextMuted, display: "block" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: colorTextPrimary,
        fontFamily: mono ? "monospace" : undefined,
        wordBreak: "break-all",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const EmptyState = ({ label }: { label: string }) => (
  <Typography variant="body2" sx={{ color: colorTextMuted }}>
    {label}
  </Typography>
);

const SummaryTable = ({
  columns,
  rows,
  monoCol,
}: {
  columns: string[];
  rows: string[][];
  monoCol?: number;
}) => (
  <Table size="small" sx={{ mt: -1 }}>
    <TableHead>
      <TableRow>
        {columns.map((col) => (
          <TableCell key={col} sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}>
            {col}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, i) => (
        <TableRow key={i} sx={{ "&:last-child td": { borderBottom: 0 } }}>
          {row.map((cell, j) => (
            <TableCell
              key={j}
              sx={{
                pl: 0,
                color: j === 0 ? colorTextPrimary : colorTextMuted,
                fontFamily: j === monoCol ? "monospace" : undefined,
                fontSize: j === monoCol ? 12 : undefined,
                wordBreak: j === monoCol ? "break-all" : undefined,
              }}
            >
              {cell}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
